/** A floating panel's top-left corner in window pixels. */
export interface PanelPosition {
    x: number;
    y: number;
}

/** Gap between a toolbar's edge and the panel that opens beside it. */
const TOOLBAR_GAP_PX = 26;
/** The least distance a panel keeps from the window's sides and bottom. */
export const WINDOW_MARGIN_PX = 16;
/** The least distance a panel keeps from the window's top edge. */
const TOP_MARGIN_PX = 50;
/**
 * How much of a remembered panel must remain below the window's bottom
 * edge for the position to be reused: enough for its header to be grabbed.
 */
const MIN_VISIBLE_HEIGHT_PX = 100;

/**
 * Where a floating panel of `panelWidth` opens beside a toolbar, top-aligned
 * with it: to the toolbar's right, or to its left when the right would fall
 * past the window edge. The toolbar anchors to the viewport area's right
 * edge, which is the window edge on the grid and in fullscreen, so without
 * the fallback the panel would open off-screen there.
 */
export function panelPositionBesideToolbar(
    toolbar: DOMRect,
    panelWidth: number
): PanelPosition {
    const preferredX = toolbar.right + TOOLBAR_GAP_PX;
    const maxX = window.innerWidth - panelWidth - WINDOW_MARGIN_PX;
    return {
        x:
            preferredX > maxX
                ? Math.max(
                      WINDOW_MARGIN_PX,
                      toolbar.left - panelWidth - TOOLBAR_GAP_PX
                  )
                : preferredX,
        y: Math.max(TOP_MARGIN_PX, toolbar.top),
    };
}

/**
 * Where a floating panel of `panelWidth` opens when nothing anchors it: the
 * window's top-right corner, inside the margins.
 */
export function panelPositionAtTopRight(panelWidth: number): PanelPosition {
    return {
        x: Math.max(
            WINDOW_MARGIN_PX,
            window.innerWidth - panelWidth - WINDOW_MARGIN_PX
        ),
        y: TOP_MARGIN_PX,
    };
}

/**
 * Whether a remembered position puts a panel of `panelWidth` on screen:
 * its whole width inside the window and enough height below the top for
 * its header to be grabbed.
 */
function isOnScreen(saved: PanelPosition, panelWidth: number): boolean {
    return (
        saved.x >= 0 &&
        saved.y >= 0 &&
        saved.x + panelWidth <= window.innerWidth &&
        saved.y + MIN_VISIBLE_HEIGHT_PX <= window.innerHeight
    );
}

/**
 * The position a panel of `panelWidth` reopens at: the remembered one
 * while that is on screen, else `defaultPosition()`.
 */
export function savedPanelPositionOrDefault<T extends PanelPosition | null>(
    saved: PanelPosition | null,
    panelWidth: number,
    defaultPosition: () => T
): PanelPosition | T {
    return saved && isOnScreen(saved, panelWidth) ? saved : defaultPosition();
}

/** `position` moved by a drag's delta. */
export function panelPositionMovedBy(
    position: PanelPosition,
    delta: { x: number; y: number }
): PanelPosition {
    return { x: position.x + delta.x, y: position.y + delta.y };
}
